import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, LifeBuoy, Compass, Lightbulb, Package, Tag, PlusCircle, TrendingUp } from 'lucide-react';
import { matchTourStep } from './tourSteps';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1a36d5 0%, #1c56cc 100%)';

const GREETING = {
  role: 'assistant',
  content: "Hi, I'm the SCN assistant 👋 Ask me anything: scanning, listing, shipping, profit. And if you're ever stuck, I'll get a real person on it.",
};
const SUGGESTIONS = [
  { Icon: Package, label: 'How do I buy a shipping label?' },
  { Icon: Tag, label: 'How do I list a card to eBay?' },
  { Icon: PlusCircle, label: 'How do I add a card manually?' },
  { Icon: TrendingUp, label: 'Where do I see my real profit?' },
];

// Small round bot avatar used in the header + next to assistant bubbles.
function BotAvatar({ size = 26, iconSize = 13 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: BRAND_GRADIENT, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      boxShadow: '0 2px 6px rgba(26,54,213,0.35)',
    }}>
      <Sparkles size={iconSize} />
    </div>
  );
}

export default function AssistantWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'compose' | 'sent'
  const [composeKind, setComposeKind] = useState('team'); // 'team' | 'suggestion'
  const [contactText, setContactText] = useState('');
  const [sending, setSending] = useState(false);
  // PageTour sets window.__scnHasPageTour on pages that have a guided tour.
  // We read it when the widget opens so we only offer the tour when one exists.
  const [hasTour, setHasTour] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  // On mobile the fixed bottom-nav (~72px, bottom:0) sits under us, lift the FAB
  // and the open panel above it so we don't cover the nav tabs.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const fabBottom = isMobile ? 88 : 20;

  useEffect(() => {
    if (open && view === 'chat') {
      // Only auto-scroll once a real conversation starts; on first open the
      // greeting + quick questions should be seen from the top.
      if (messages.length > 1 || loading) endRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, open, view, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next);
    setInput('');
    setLoading(true);
    // Does this question map to a guided step? If so, attach it to the reply so we
    // can offer a "Show me how" button that fires just that spotlight.
    const tourStep = matchTourStep(msg);
    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        page: window.location.pathname,
      });
      setMessages([...next, { role: 'assistant', content: res.data.reply, tourStep }]);
    } catch (e) {
      setMessages([...next, { role: 'assistant', content: "Sorry, I'm having trouble right now. Try again in a moment, or message the team below.", tourStep }]);
    } finally {
      setLoading(false);
    }
  };

  // "Show me how", navigate to the step's page if needed, then fire the single
  // spotlight. Double-dispatch covers a slow page mount (PageTour ignores the
  // event until its route's tourKey matches).
  const launchStep = (m) => {
    if (!m) return;
    setOpen(false);
    const fire = () => window.dispatchEvent(new CustomEvent('scn-tour-goto', { detail: { tourKey: m.tourKey, stepId: m.stepId } }));
    const here = window.location.pathname + window.location.search;
    if (here !== m.path) {
      navigate(m.path);
      setTimeout(fire, 500);
      setTimeout(fire, 1200);
    } else {
      setTimeout(fire, 80);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const sendContact = async () => {
    const msg = contactText.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/assistant/contact`, {
        message: msg,
        kind: composeKind === 'suggestion' ? 'suggestion' : undefined,
        // A straight-to-the-box suggestion has no chat to attach; only send the
        // conversation when they actually talked to the bot first.
        conversation: messages.length > 1 ? messages.map((m) => ({ role: m.role, content: m.content })) : undefined,
        page: window.location.pathname,
      });
      setView('sent');
      setContactText('');
    } catch (e) {
      alert("Couldn't send right now, please email support@sportscardnetwork.ai.");
    } finally {
      setSending(false);
    }
  };

  const startCompose = (kind = 'team') => {
    setComposeKind(kind);
    if (kind === 'suggestion') {
      // Suggestions start blank — they're an idea, not the last question.
      setContactText('');
    } else {
      // Pre-fill with their last question for convenience
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      setContactText(lastUser ? lastUser.content : '');
    }
    setView('compose');
  };

  const openWidget = () => {
    setHasTour(typeof window !== 'undefined' && !!window.__scnHasPageTour);
    setOpen(true);
  };

  // Fire the current page's guided tour, then close so it's visible. The chat
  // bubble stays put, so they land right back here when the tour ends.
  const startPageTour = () => {
    setOpen(false);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('scn-replay-tour'));
  };

  const footerBtn = {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '7px 10px',
    fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer',
  };

  return (
    <>
      {!open && (
        <button onClick={openWidget} aria-label="Open SCN assistant" style={{
          position: 'fixed', bottom: fabBottom, right: 20, zIndex: 1200, width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: BRAND_GRADIENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 20px rgba(26,54,213,0.4)',
        }}>
          <MessageCircle size={26} />
        </button>
      )}

      {open && (
        <div className="scn-assistant-panel" style={{
          position: 'fixed', bottom: fabBottom, right: 20, zIndex: 1200, width: 'min(380px, calc(100vw - 32px))', height: isMobile ? 'min(580px, calc(100vh - 124px))' : 'min(580px, calc(100vh - 40px))',
          background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 18px 50px rgba(15,23,42,0.28)', display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #16277e 0%, #1a36d5 55%, #1c56cc 100%)', color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={19} />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.1 }}>SCN Assistant</div>
                <div style={{ fontSize: 11.5, opacity: 0.92, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 0 2px rgba(74,222,128,0.3)' }} />
                  Online, replies instantly
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', cursor: 'pointer', padding: 6, borderRadius: 8, display: 'flex' }}>
              <X size={18} />
            </button>
          </div>

          {/* COMPOSE view */}
          {view === 'compose' && (
            <div style={{ flex: 1, padding: 18, background: 'linear-gradient(180deg, #eef2fb 0%, #f8fafc 140px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: composeKind === 'suggestion' ? '#fef3c7' : '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {composeKind === 'suggestion' ? <Lightbulb size={20} color="#b45309" /> : <LifeBuoy size={20} color="#1a36d5" />}
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 800, color: '#0f172a' }}>{composeKind === 'suggestion' ? 'Suggest a feature' : 'Message the team'}</div>
              </div>
              <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.55 }}>
                {composeKind === 'suggestion'
                  ? "Got an idea, a feature you wish SCN had, or something that bugs you? It goes straight to Zack, the founder. He reads every one, and dealer suggestions drive what gets built next."
                  : 'Type your question and it goes straight to us. Zack, the founder, is online most of the day and usually replies the same day, right to your account email.'}
              </div>
              <textarea value={contactText} onChange={(e) => setContactText(e.target.value)} autoFocus placeholder={composeKind === 'suggestion' ? 'What should SCN do better?' : 'What do you need help with?'}
                style={{ flex: 1, minHeight: 120, border: '1px solid #cbd5e1', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, background: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setView('chat')} style={{ flex: 1, background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px', cursor: 'pointer', fontSize: 13.5, fontWeight: 600 }}>Back</button>
                <button onClick={sendContact} disabled={sending || !contactText.trim()} style={{ flex: 2, background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 10, padding: '10px', cursor: sending || !contactText.trim() ? 'default' : 'pointer', opacity: sending || !contactText.trim() ? 0.5 : 1, fontSize: 13.5, fontWeight: 700, boxShadow: '0 3px 10px rgba(26,54,213,0.3)' }}>{sending ? 'Sending…' : (composeKind === 'suggestion' ? 'Send suggestion' : 'Send to team')}</button>
              </div>
            </div>
          )}

          {/* SENT confirmation */}
          {view === 'sent' && (
            <div style={{ flex: 1, padding: 24, background: 'linear-gradient(180deg, #eef2fb 0%, #f8fafc 140px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: composeKind === 'suggestion' ? '#fef3c7' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, boxShadow: '0 4px 14px rgba(15,23,42,0.1)' }}>
                {composeKind === 'suggestion' ? '💡' : '✅'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{composeKind === 'suggestion' ? 'Suggestion sent!' : 'Message sent!'}</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55, maxWidth: 280 }}>
                {composeKind === 'suggestion'
                  ? 'It went straight to Zack, the founder. Dealer suggestions are where most SCN features come from. Thanks for taking the time.'
                  : "It went straight to Zack and the team. You'll usually hear back the same day at your account email, so keep an eye on your inbox."}
              </div>
              <button onClick={() => setView('chat')} style={{ background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 10, padding: '9px 20px', cursor: 'pointer', fontSize: 13.5, fontWeight: 700, boxShadow: '0 3px 10px rgba(26,54,213,0.3)' }}>Back to chat</button>
            </div>
          )}

          {/* CHAT view */}
          {view === 'chat' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: 14, background: 'linear-gradient(180deg, #eef2fb 0%, #f8fafc 160px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.map((m, i) => (
                  m.role === 'user' ? (
                    <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                      <div style={{
                        padding: '9px 13px', borderRadius: 16, borderBottomRightRadius: 5, fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                        background: BRAND_GRADIENT, color: '#fff', boxShadow: '0 2px 8px rgba(26,54,213,0.25)',
                      }}>{m.content}</div>
                    </div>
                  ) : (
                    <div key={i} style={{ alignSelf: 'flex-start', maxWidth: '88%', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <BotAvatar />
                      <div>
                        <div style={{
                          padding: '10px 13px', borderRadius: 16, borderBottomLeftRadius: 5, fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                          background: '#fff', color: '#1e293b', border: '1px solid #e6eaf3', boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                        }}>{m.content}</div>
                        {m.tourStep && (
                          <button onClick={() => launchStep(m.tourStep)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 12.5, fontWeight: 700,
                            color: '#fff', background: BRAND_GRADIENT, border: 'none',
                            borderRadius: 10, padding: '8px 12px', cursor: 'pointer', textAlign: 'left', boxShadow: '0 3px 10px rgba(26,54,213,0.3)',
                          }}>
                            <Compass size={14} /> Show me how
                          </button>
                        )}
                      </div>
                    </div>
                  )
                ))}

                {messages.length === 1 && !loading && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2, marginLeft: 34 }}>
                    {hasTour && (
                      <button onClick={startPageTour} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#fff', background: BRAND_GRADIENT, border: 'none', borderRadius: 12, padding: '9px 13px', cursor: 'pointer', textAlign: 'left', boxShadow: '0 3px 10px rgba(26,54,213,0.3)' }}>
                        <Compass size={15} /> Show me around this page
                      </button>
                    )}
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.7, margin: '3px 0 0 2px' }}>Popular questions</div>
                    {SUGGESTIONS.map(({ Icon, label }) => (
                      <button key={label} onClick={() => send(label)} style={{
                        display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, fontWeight: 600, color: '#334155',
                        background: '#fff', border: '1px solid #e6eaf3', borderRadius: 11, padding: '7px 11px', cursor: 'pointer',
                        textAlign: 'left', boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
                      }}>
                        <span style={{ width: 23, height: 23, borderRadius: 7, background: '#eef1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={13} color="#1a36d5" />
                        </span>
                        <span style={{ flex: 1 }}>{label}</span>
                        <span style={{ color: '#94a3b8', fontSize: 15, lineHeight: 1 }}>›</span>
                      </button>
                    ))}
                  </div>
                )}

                {loading && (
                  <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <BotAvatar />
                    <div style={{ background: '#fff', border: '1px solid #e6eaf3', borderRadius: 16, borderBottomLeftRadius: 5, padding: '12px 14px', display: 'flex', gap: 4, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
                      <span className="scn-typing-dot" />
                      <span className="scn-typing-dot" style={{ animationDelay: '0.15s' }} />
                      <span className="scn-typing-dot" style={{ animationDelay: '0.3s' }} />
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Input + footer actions */}
              <div style={{ borderTop: '1px solid #e8edf5', padding: '10px 12px 9px', background: '#fff' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#f8fafc', border: '1px solid #dbe3ef', borderRadius: 999, padding: '4px 4px 4px 15px' }}>
                  <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} placeholder="Ask anything about SCN…"
                    style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13.5, outline: 'none', padding: '7px 0' }} />
                  <button onClick={() => send()} disabled={loading || !input.trim()} aria-label="Send" style={{
                    background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    cursor: loading || !input.trim() ? 'default' : 'pointer', opacity: loading || !input.trim() ? 0.45 : 1, boxShadow: '0 2px 8px rgba(26,54,213,0.3)',
                  }}><Send size={15} /></button>
                </div>
                <div style={{ marginTop: 9, display: 'flex', gap: 8 }}>
                  <button onClick={() => startCompose('team')} style={footerBtn}>
                    <LifeBuoy size={13} color="#1a36d5" /> Message the team
                  </button>
                  <button onClick={() => startCompose('suggestion')} style={footerBtn}>
                    <Lightbulb size={13} color="#b45309" /> Suggest a feature
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
